import Reveal from "@Components/Reveal/Reveal";
import Image from "@Components/Image";
import { pageAsset } from "@Utils/getStaticAssetPath";
import styles from "./AmbassadorVoices.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * Illustrated avatars, deliberately NOT photographs — the same call
 * ListWithUsV2's testimonials made, and for the same reason.
 *
 * These three ambassadors are real, named people at named universities. We have
 * no headshots for them, and putting a stock photo of a student beside "Shivali
 * Choyal, University of Strathclyde" is a claim about what a named person looks
 * like. A drawing makes no such claim.
 *
 * Pointed at ListWithUsV2's set rather than copied onto this page's prefix, so
 * there is one copy of each face on the bucket. Faces 02/07/11 are unused by that
 * page, so the two sections do not show the same three drawings.
 */
const listAsset = pageAsset("ListWithUsV2");

/**
 * What the ambassadors say.
 *
 * ⚠️  THE QUOTES ARE CONDENSED, NOT INVENTED. Each one was supplied as three or
 * four paragraphs; these are two sentences each, kept in the ambassador's own
 * phrasing and carrying only claims they actually made. The full versions are
 * worth keeping wherever they came from — a longer quote may suit a case-study
 * page even though it does not suit a card.
 *
 * The quotes are `ambassador.voices.<id>.quote` keys, in all ten locales (the non-English
 * ones machine-written). Names and universities are proper nouns and stay as written.
 */
const VOICES = [
  {
    id: "shivali",
    name: "Shivali Choyal",
    role: "University of Strathclyde",
    avatar: listAsset("avatars/face-02.png"),
    quote: "ambassador.voices.shivali.quote",
  },
  {
    id: "anshi",
    name: "Anshi Agarwala",
    role: "Adelaide University",
    avatar: listAsset("avatars/face-07.png"),
    quote: "ambassador.voices.anshi.quote",
  },
  {
    id: "simran",
    name: "Simran Panda",
    role: "University of Birmingham",
    avatar: listAsset("avatars/face-11.png"),
    quote: "ambassador.voices.simran.quote",
  },
];

const Voices = () => {
  const { t } = useTranslationHook();

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* `inview`, not the default `early`: a section heading is scrolled to
            deliberately, and the default trigger fires a fifth of a screen below
            the fold — so its 0.7s entrance was over before the heading arrived
            and read as no animation at all. The cards and list items below keep
            the default, where a reader should never wait on a transition. */}
        <Reveal className={styles.head} trigger="inview">
          <h2 className={styles.heading}>{t("ambassador.testimonials.title")}</h2>
          <p className={styles.lede}>{t("ambassador.testimonials.subtitle")}</p>
        </Reveal>

        <ul className={styles.grid}>
          {VOICES.map((voice, i) => (
            <Reveal as="li" className={styles.card} key={voice.id} delay={i * 90}>
              {/* ⚠️  QUOTE FIRST, PERSON IN A FOOTER — the property page's review
                  card, the same treatment `/podcast`'s listener reviews use. This
                  card put the person block ABOVE the quote on a plain white panel;
                  the comment there said knowing who is speaking first is what
                  makes a testimonial worth reading, and that call has been
                  reversed: the quote is what earns the read, and the attribution
                  is what you check after it. */}
              <div className={styles.content}>
                <p className={styles.quote}>{t(voice.quote)}</p>
              </div>

              <div className={styles.footer}>
                <Image src={voice.avatar} alt="" className={styles.avatar} width={42} height={42} />
                <div className={styles.person}>
                  <span className={styles.name}>{voice.name}</span>
                  <span className={styles.role}>{voice.role}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default wrapperHOC(Voices, {
  componentName: "Voices-AmbassadorV2",
  showForChina: true,
});
